import { useEffect, useState } from 'react';
import { Smartphone, X, Share, Plus } from 'lucide-react';

/**
 * PWA Install Prompt
 *  - Android/Chrome: listens for `beforeinstallprompt` and shows a custom banner
 *  - iOS Safari: shows instructions banner (Safari does not support the install event)
 *  - Dismissed state persists in localStorage so we don't nag
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState('android'); // 'android' | 'ios'

  useEffect(() => {
    const dismissed = localStorage.getItem('qb_pwa_dismissed');
    if (dismissed) {
      const daysSince = (Date.now() - new Date(dismissed).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 14) return; // Respect dismissal for 14 days
    }

    // Already installed — do not show prompt
    if (window.matchMedia?.('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);

    if (isIOS) {
      setPlatform('ios');
      // Show after a short delay so it doesn't interrupt initial load
      const t = setTimeout(() => setIsVisible(true), 4000);
      return () => clearTimeout(t);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform('android');
      // Only show on mobile-ish devices
      if (isAndroid || window.innerWidth < 900) {
        setIsVisible(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => {
      setIsVisible(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem('qb_pwa_dismissed', new Date().toISOString());
    setIsVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (e) {
      // no-op
    }
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[90] md:bottom-6 md:left-auto md:right-6 md:max-w-sm"
      data-testid="pwa-install-prompt"
    >
      <div className="relative bg-[#0A0A0A] border-2 border-[#D4AF37]/50 p-4 shadow-2xl shadow-black/50">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 p-1.5 text-white/40 hover:text-white/80 hover:bg-white/5"
          aria-label="Dismiss"
          data-testid="pwa-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-[#D4AF37]" />
          </div>

          <div className="flex-1 min-w-0 pr-6">
            <h3 className="text-white font-bold text-sm mb-1">
              Add the Hive to your home screen
            </h3>

            {platform === 'ios' ? (
              <div className="text-white/60 text-xs space-y-1.5 mt-2">
                <p>Tap <Share className="w-3.5 h-3.5 inline text-[#D4AF37]" /> in Safari, then:</p>
                <p className="flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span>"Add to Home Screen"</span>
                </p>
              </div>
            ) : (
              <>
                <p className="text-white/60 text-xs mb-3">
                  Launch the Hive full-screen like an app. No app store needed.
                </p>
                <button
                  onClick={install}
                  className="w-full py-2 bg-[#D4AF37] text-black font-bold text-xs uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
                  data-testid="pwa-install-btn"
                >
                  Install the Hive
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
